import { DeviationTypes } from '../../types/deviation';
function toSrcFile(file) {
    if (!file) {
        return undefined;
    }
    return {
        type: file.type,
        src: file.src,
        height: file.height,
        width: file.width,
        transparency: file.transparency,
        quality: file.quality,
        filesize: file.filesize,
        duration: file.duration,
        isDefault: file.isDefault
    };
}
const defaultFullview = {
    type: 'unknown',
    src: '',
    width: 1000,
    height: 1000,
    isDefault: true
};
/** @deprecated */ class LegacyMediaHandler {
    isGif(deviation) {
        if (deviation && deviation.isGif) {
            return true;
        }
        if (!deviation.files) {
            return false;
        }
        return deviation.files.find((file)=>file.type === 'gif') ? true : false;
    }
    getDeviationType(deviation) {
        if (!deviation) {
            return DeviationTypes.UNKNOWN;
        }
        // New type handling backend now defines
        if (deviation.type) {
            return deviation.type;
        }
        // Legacy duck typing below
        // polls
        if (deviation.poll) {
            return DeviationTypes.POLL;
        }
        // commission
        if (deviation.commission) {
            return DeviationTypes.COMMISSION;
        }
        // status
        if (deviation.status) {
            return DeviationTypes.STATUS;
        }
        // Freeform Text (Journal - Status - Literature)
        if (deviation.textContent) {
            if (deviation.isJournal) {
                return DeviationTypes.JOURNAL;
            }
            return DeviationTypes.LITERATURE;
        }
        if (!deviation.files) {
            return DeviationTypes.UNKNOWN;
        }
        const fileTypes = {};
        let defaultFilesCount = 0;
        for (const file of deviation.files){
            fileTypes[file.type] = true;
            if (file.isDefault) {
                defaultFilesCount++;
            }
        }
        // Video
        if (deviation.isVideo || fileTypes.video) {
            return DeviationTypes.FILM;
        }
        // PDF
        if (fileTypes.pdf) {
            return DeviationTypes.PDF;
        }
        // GIF
        if (fileTypes.gif) {
            return DeviationTypes.IMAGE;
        }
        // Image
        if (defaultFilesCount !== deviation.files.length) {
            return DeviationTypes.IMAGE;
        }
        return DeviationTypes.UNKNOWN;
    }
    getSmallestThumb(deviation, options) {
        const thumbs = this.getThumbSet(deviation, options);
        if (!thumbs) {
            return;
        }
        const small = this._getThumbsSortedSmallToBig(thumbs)[0];
        return toSrcFile(small);
    }
    getSmallestThumbThatFullyCovers(deviation, width, height, fallback, options) {
        const thumbs = this.getThumbSet(deviation, options);
        if (!thumbs) {
            return;
        }
        // Allow slightly smaller than the requested block, the scaling
        // rules allow for 25%~ smaller to be scaled up so make this match
        // those. We force portraits to match the width.
        let allowedWidth = width; // * 0.85;
        const allowedHeight = height; // * 0.85;
        if (width < height) {
            allowedWidth = width;
        }
        const smallest = this._getThumbsSortedSmallToBig(thumbs).find((img)=>{
            const { width: imgWidth = 0, height: imgHeight = 0 } = img;
            return imgWidth >= allowedWidth && imgHeight >= allowedHeight;
        });
        // Allow caller to determine the fallback
        if (!smallest && fallback) {
            return fallback(thumbs);
        }
        return smallest && toSrcFile(smallest);
    }
    getSmallestThumbForArtStage(deviation, width, height) {
        // GIF has to use the fullview
        if (this.isGif(deviation)) {
            return this.getFullviewForSize(deviation, width, height);
        }
        // For thumbs as its meant to be a fullview we avoid crops and fits
        const thumbsAll = this.getThumbSet(deviation, {
            allowPreview: true
        });
        const thumbs = thumbsAll.filter((f)=>f.src.includes('/fill/'));
        if (!thumbs) {
            return;
        }
        // Get the smallest thumb this is bigger than the stage in any direction
        const smallest = this._getThumbsSortedSmallToBig(thumbs).find((img)=>{
            const { width: imgWidth = 0, height: imgHeight = 0 } = img;
            return imgWidth >= width || imgHeight >= height;
        });
        // Allow caller to determine the fallback
        if (!smallest) {
            return this.getFullviewForSize(deviation, width, height);
        }
        return smallest;
    }
    getLargestFullview(deviation) {
        const type = this.getDeviationType(deviation);
        if (type === DeviationTypes.JOURNAL || type === DeviationTypes.LITERATURE) {
            return defaultFullview;
        }
        return this._getLargestFullview(deviation);
    }
    getThumbDimensions(deviation) {
        const fv = this._getLargestFullview(deviation);
        const type = this.getDeviationType(deviation);
        const defaultDimensions = {
            width: 1000,
            height: 1000
        };
        if (type === DeviationTypes.TIER) {
            return {
                width: 1840,
                height: 1000
            };
        }
        if (!fv || !fv.width || !fv.height || [
            DeviationTypes.JOURNAL,
            DeviationTypes.LITERATURE,
            DeviationTypes.STATUS,
            DeviationTypes.COMMISSION
        ].includes(type)) {
            return defaultDimensions;
        }
        return {
            width: fv.width,
            height: fv.height
        };
    }
    getFullviewForSize(deviation, width, height) {
        const fullviews = this._getFullviews(deviation);
        if (!fullviews.length) {
            return;
        }
        // sort by resolution small to big
        fullviews.sort((a, b)=>this._getFilePixelSize(a) - this._getFilePixelSize(b));
        // find smallest fitting file
        if (width && height) {
            const file = fullviews.find((f)=>{
                if (!f.width || !f.height) {
                    return false;
                }
                return f.width >= width && f.height >= height;
            });
            if (file) {
                return toSrcFile(file);
            }
        }
        // if no fitting file was found return largest one
        return toSrcFile(fullviews[fullviews.length - 1]);
    }
    getDeviationFile(deviation, fileType) {
        const files = deviation && deviation.files;
        const file = files && files.find((f)=>f.type === fileType);
        if (file) {
            return toSrcFile(file);
        }
        return undefined;
    }
    getThumbSet(deviation, options) {
        const thumbFileTypes = [
            '150',
            '200H',
            '300W',
            '200T',
            '250T',
            '350T',
            '400T',
            '375W',
            '414W',
            '750W',
            '828W'
        ];
        // Check if this deviation may be compressed by the editor, in this
        // case we can have reduced files array and need to allow
        if (this._mightBeCompressedFilesList(deviation)) {
            thumbFileTypes.push('preview', 'fullview', 'gif');
        }
        if (options && options.allowPreview) {
            thumbFileTypes.push('preview');
        }
        if (options && options.allowFullview) {
            thumbFileTypes.push('fullview');
            thumbFileTypes.push('fullview_unpublished');
        }
        if (options && options.allowSquareCrop) {
            thumbFileTypes.push('92S');
            thumbFileTypes.push('125S');
        }
        if (!deviation || !deviation.files) {
            return [];
        }
        return deviation.files.filter((file)=>!file.isDefault && thumbFileTypes.includes(file.type));
    }
    getMediaByType(deviation, fileType) {
        if (!deviation || !deviation.files || !Array.isArray(deviation.files)) {
            return [];
        }
        return deviation.files.filter((file)=>file && file.type === fileType);
    }
    getSrcSetForDeviationPreview(deviation, isMobile, screenVw) {
        throw new Error('Legacy not supported');
        return {
            sizes: '',
            srcSet: '',
            src: '',
            srcFile: '',
            previewFile: ''
        };
    }
    _getFullviews(deviation) {
        if (!deviation || !deviation.files) {
            return [];
        }
        const files = deviation && deviation.files;
        // for gif images only the gif file is a valid fullview
        const gif = files && files.find((f)=>f.type === 'gif');
        if (gif) {
            return [
                gif
            ];
        }
        return files.filter((file)=>file && /^fullview|preview|original/.test(file.type));
    }
    _getFilePixelSize(file) {
        return (file.width || 0) * (file.height || 0);
    }
    _getThumbsSortedSmallToBig(thumbs) {
        if (!thumbs) {
            return [];
        }
        return thumbs.sort((a, b)=>{
            const asc = (a.width || 0) * (a.height || 0);
            const bsc = (b.width || 0) * (b.height || 0);
            if (asc === bsc) {
                return 0;
            }
            return asc < bsc ? -1 : 1;
        });
    }
    _mightBeCompressedFilesList(deviation) {
        if (!deviation || !deviation.files) {
            return false;
        }
        // Assume single file whatever it is to be safe
        if (deviation.files.length === 1) {
            return true;
        }
        const types = deviation.files.map((f)=>f.type);
        // social_preview bug T38231
        if (types.length === 2 && types.includes('fullview') && types.includes('social_preview')) {
            return true;
        }
        return false;
    }
    _getLargestFullview(deviation) {
        const fvs = this._getFullviews(deviation);
        if (fvs.length === 0) {
            return defaultFullview;
        }
        const fv = fvs.reduce((prev, curr)=>{
            if ((prev.width || 0) > (curr.width || 0) || (prev.height || 0) > (curr.height || 0)) {
                return prev;
            }
            return curr;
        });
        return fv && toSrcFile(fv) || defaultFullview;
    }
}
export default LegacyMediaHandler;
